export class ReferralService {
  private static instance: ReferralService;
  private referralCodes: Map<string, ReferralCode> = new Map();
  private redemptions: Map<string, ReferralRedemption> = new Map();

  private constructor() {}

  static getInstance(): ReferralService {
    if (!ReferralService.instance) {
      ReferralService.instance = new ReferralService();
    }
    return ReferralService.instance;
  }

  generateReferralCode(groupId: string): ReferralCode {
    const code = this.generateUniqueCode();
    const referral: ReferralCode = {
      id: crypto.randomUUID(),
      code,
      groupId,
      usageCount: 0,
      createdAt: new Date(),
    };

    this.referralCodes.set(code, referral);
    return referral;
  }

  private generateUniqueCode(): string {
    // Generate a 6-character alphanumeric code
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code: string;
    
    do {
      code = Array.from({ length: 6 }, () =>
        characters.charAt(Math.floor(Math.random() * characters.length))
      ).join('');
    } while (this.referralCodes.has(code));

    return code;
  }

  async validateReferralCode(code: string): Promise<ReferralCode | null> {
    const referral = this.referralCodes.get(code);
    if (!referral) return null;

    if (referral.expiresAt && referral.expiresAt < new Date()) return null;
    if (referral.maxUses && referral.usageCount >= referral.maxUses) return null;

    return referral;
  }

  async redeemReferralCode(code: string, newGroupId: string): Promise<ReferralRedemption> {
    const referral = await this.validateReferralCode(code);
    if (!referral) throw new Error('Invalid referral code');

    const redemption: ReferralRedemption = {
      id: crypto.randomUUID(),
      referralId: referral.id,
      referrerGroupId: referral.groupId,
      newGroupId,
      redeemedAt: new Date(),
      discountApplied: {
        referrer: false,
        referred: false,
      },
    };

    referral.usageCount += 1;
    this.referralCodes.set(code, referral);
    this.redemptions.set(redemption.id, redemption);

    // Apply discounts
    await this.applyReferralDiscounts(redemption);

    return redemption;
  }

  private async applyReferralDiscounts(redemption: ReferralRedemption): Promise<void> {
    // In production, this would integrate with the billing system
    // to apply a 100% discount for one month to both groups
    redemption.discountApplied = {
      referrer: true,
      referred: true,
    };
    this.redemptions.set(redemption.id, redemption);
  }

  getReferralsByGroup(groupId: string): ReferralCode[] {
    return Array.from(this.referralCodes.values()).filter(
      (referral) => referral.groupId === groupId
    );
  }

  getRedemptionsByGroup(groupId: string): ReferralRedemption[] {
    return Array.from(this.redemptions.values()).filter(
      (redemption) =>
        redemption.referrerGroupId === groupId || redemption.newGroupId === groupId
    );
  }
}